/**
 * "Needs action": the orders in one lane that are not done, most urgent first,
 * each with the plain-words reason, and a detail pane for the one selected.
 *
 * The lane (Deliveries, Collections or ETAs) is chosen on the cards above and
 * is named in the panel's own header and detail pane, so the list can never be
 * read as belonging to a different lane. Every open order is in the list: the
 * default view shows the ones that need action, and "All open" adds the ones
 * that are on track, so the count on the card always matches a list.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  directionLabel,
  formatLifts,
  formatOrdersDue,
  formatOrdersImpact,
  formatOrdersService,
  journeyTrailSummary,
  statusCodeLabel,
} from '../../utils/ordersDailyPresentation';
import {
  countNeedingAction,
  type OrdersExceptionItem,
  type OrdersExceptionLane,
} from '../../utils/ordersExceptions';
import { ETA_WARNING_OPTIONS, type EtaWarningMinutes } from '../../types/ordersUiPreferences';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

const LANE_TEXT: Record<OrdersExceptionLane, { name: string; heading: string; none: string; pill: string }> = {
  deliveries: {
    name: 'Deliveries',
    heading: 'Deliveries not done',
    none: 'No deliveries need attention.',
    pill: 'border-success/60 text-success',
  },
  collections: {
    name: 'Collections',
    heading: 'Collections not done',
    none: 'No collections need attention.',
    pill: 'border-warning/60 text-warning',
  },
  etas: {
    name: 'ETAs',
    heading: 'Outside their ETA, or about to be',
    none: 'No orders are outside their ETA.',
    pill: 'border-ring/60 text-ring',
  },
};

// Severity is status-meaningful; opacity modifiers preserved.
const SEVERITY_TAG: Record<0 | 1 | 2 | 3, string> = {
  3: 'bg-destructive/15 text-destructive',
  2: 'bg-warning/15 text-warning',
  1: 'bg-ring/15 text-ring',
  0: 'bg-secondary/60 text-foreground',
};

const SEVERITY_DOT: Record<0 | 1 | 2 | 3, string> = {
  3: 'bg-destructive',
  2: 'bg-warning',
  1: 'bg-ring',
  0: 'bg-muted-foreground',
};

function itemKey(item: OrdersExceptionItem): string {
  return `${item.row.direction}|${item.row.tracking_id}`;
}

function Tag({ item }: { item: OrdersExceptionItem }) {
  return (
    <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap ${SEVERITY_TAG[item.severity]}`}>
      {item.cause ?? 'On track'}
    </span>
  );
}

/**
 * Rows shown before the list scrolls. "All open" can run past a hundred on a
 * busy day (133 open deliveries on stage, 26 Oct 2026); the list scrolls in
 * its own box so the panel never pushes the rest of the page away. The box
 * (max-h-88) holds about this many rows, level with the detail pane.
 */
export const LIST_ROWS_BEFORE_SCROLL = 6;

function Detail({
  item, onOpenInTable,
}: { item: OrdersExceptionItem; onOpenInTable: (trackingId: string) => void }) {
  const { row } = item;
  const route = [row.col_depot, row.del_depot].filter(Boolean).join(' to ');
  const lastScan = statusCodeLabel(row.latest_status_code);
  const trail = journeyTrailSummary(row);
  const fields: Array<[string, string]> = [
    ['Due', formatOrdersDue(row.due_date, row.due_time)],
    ['Service', formatOrdersService(row)],
    ['Route', route || 'Not recorded'],
    ['Load', row.lifts != null ? `${formatLifts(row.lifts)} lifts` : 'Not recorded'],
  ];
  if (row.direction === 'delivery') {
    fields.push(['ETA runs out', item.etaExpiry ? item.etaExpiry.slice(11, 16) : 'No ETA received']);
  }
  if (!row.booked_in_excluded && (row.missed_am || row.at_risk_1700)) {
    fields.push(['Impact', formatOrdersImpact(row)]);
  }
  if (row.direction === 'collection' && row.collection_source) {
    fields.push(['Booked by', row.booking_customer_label ?? row.collection_source.replace('_', ' ')]);
  }
  fields.push(['Last scan', lastScan ?? 'None yet']);
  return (
    <div className="rounded-lg border border-border bg-background/60 p-4 text-sm lg:self-start" data-testid="orders-workspace-detail">
      <div className="text-xs uppercase tracking-wide-2 text-muted-foreground">
        {directionLabel(row.direction)} · <span className="font-mono normal-case tracking-normal">{row.tracking_id}</span>
      </div>
      <div className="mt-1 flex items-center gap-2 flex-wrap">
        <span className="text-base font-semibold text-white">{row.customer || row.consignment_no || row.tracking_id}</span>
        <Tag item={item} />
        {item.alsoCauses.map((c) => (
          <span key={c} className="rounded px-2 py-0.5 text-xs bg-secondary/60 text-foreground">{c}</span>
        ))}
      </div>
      <p className="mt-2 text-foreground">{item.why}</p>
      {item.age && <p className="mt-0.5 text-xs text-muted-foreground">{item.age}</p>}
      <dl className="mt-3 grid grid-cols-7rem-1fr gap-x-3 gap-y-1.5">
        {fields.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="text-foreground">{value}</dd>
          </div>
        ))}
      </dl>
      {trail.length > 0 && (
        <ul className="mt-3 space-y-1 border-l-2 border-border pl-3 text-xs text-foreground">
          {trail.map((line) => <li key={line}>{line}</li>)}
        </ul>
      )}
      <Button
        variant="secondary"
        size="sm"
        type="button"
        onClick={() => onOpenInTable(row.tracking_id)}
        className="mt-4 rounded-md border border-border bg-card font-medium"
      >
        Open in All orders
      </Button>
    </div>
  );
}

export interface OrdersExceptionWorkspaceProps {
  lane: OrdersExceptionLane;
  items: OrdersExceptionItem[];
  loading?: boolean;
  etaWarnMinutes: EtaWarningMinutes;
  onEtaWarnChange: (minutes: EtaWarningMinutes) => void;
  /** Jump to one consignment in the All orders table. */
  onOpenInTable: (trackingId: string) => void;
  /** Open All orders filtered to this lane. */
  onOpenLane: (lane: OrdersExceptionLane) => void;
  /** Rendered at the foot, e.g. the "load stale orders" line. */
  footer?: ReactNode;
}

export default function OrdersExceptionWorkspace({
  lane, items, loading, etaWarnMinutes, onEtaWarnChange, onOpenInTable, onOpenLane, footer,
}: OrdersExceptionWorkspaceProps) {
  const text = LANE_TEXT[lane];
  const [view, setView] = useState<'needs' | 'all'>('needs');
  // Each card opens on what needs action; "All open" is not carried across cards.
  useEffect(() => { setView('needs'); }, [lane]);
  const needing = countNeedingAction(items);
  const showAllToggle = lane !== 'etas';
  const visible = useMemo(
    () => (lane === 'etas' || view === 'all' ? items : items.filter((i) => i.severity > 0)),
    [items, lane, view],
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Keep a selection that still exists; otherwise fall to the first row.
  const selected = visible.find((i) => itemKey(i) === selectedKey) ?? visible[0];
  useEffect(() => {
    if (selected && itemKey(selected) !== selectedKey) setSelectedKey(itemKey(selected));
  }, [selected, selectedKey]);
  const onTrack = items.length - needing;
  const listRef = useRef<HTMLUListElement>(null);
  // Keep the selected row in view inside the scrolling list. Only the list
  // scrolls: scrollIntoView would also move the page container (App.tsx), so
  // the page jumped to the list on load and on every card click.
  useEffect(() => {
    const list = listRef.current;
    if (!selectedKey || !list) return;
    const row = list.querySelector<HTMLElement>(`[data-item-key="${CSS.escape(selectedKey)}"]`);
    if (!row) return;
    const rowBox = row.getBoundingClientRect();
    const listBox = list.getBoundingClientRect();
    if (rowBox.top < listBox.top) list.scrollTop -= listBox.top - rowBox.top;
    else if (rowBox.bottom > listBox.bottom) list.scrollTop += rowBox.bottom - listBox.bottom;
  }, [selectedKey]);
  const scrolls = visible.length > LIST_ROWS_BEFORE_SCROLL;

  return (
    <section
      id="orders-needs-action"
      aria-label={`${text.name}: needs action`}
      className="rounded-lg border border-border bg-card/40 p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className={`rounded border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide-2 ${text.pill}`}>
          {text.name}
        </span>
        <h3 className="text-base font-semibold text-white">{text.heading}</h3>
        <span className="text-sm text-muted-foreground tabular-nums">
          {lane === 'etas' ? items.length : `${needing} need action · ${items.length} open`}
        </span>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={() => onOpenLane(lane)}
          className="ml-auto rounded-md border border-border bg-card font-medium"
        >
          Open all {text.name.toLowerCase()}
        </Button>
      </div>

      {lane === 'etas' && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-foreground">
          <label htmlFor="orders-eta-warning">Warn me before an ETA runs out</label>
          <select
            id="orders-eta-warning"
            value={etaWarnMinutes}
            onChange={(e) => onEtaWarnChange(Number(e.target.value) as EtaWarningMinutes)}
            className="rounded-md border border-border bg-card px-2 py-1 text-sm text-foreground"
          >
            {ETA_WARNING_OPTIONS.map((o) => (
              <option key={o.minutes} value={o.minutes}>{o.label}</option>
            ))}
          </select>
        </div>
      )}

      {showAllToggle && items.length > 0 && (
        <div className="mt-3 inline-flex gap-1" role="group" aria-label="Which open orders to list">
          {([['needs', `Needs action ${needing}`], ['all', `All open ${items.length}`]] as const).map(([id, label]) => (
            <Button
              key={id}
              variant="ghost"
              size="sm"
              type="button"
              aria-pressed={view === id}
              onClick={() => setView(id)}
              className={cn(
                'rounded-md font-medium',
                view === id
                  ? 'bg-chart-5/20 text-chart-5 ring-1 ring-chart-5/40'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60',
              )}
            >
              {label}
            </Button>
          ))}
        </div>
      )}

      {loading && items.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading orders…</p>
      ) : visible.length === 0 ? (
        <div className="mt-4 text-sm text-foreground">
          <p>{text.none}</p>
          {showAllToggle && view === 'needs' && onTrack > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              {onTrack.toLocaleString()} open and on track.{' '}
              <Button variant="link" size="sm" type="button" className="text-ring hover:text-ring" onClick={() => setView('all')}>
                Show them
              </Button>
            </p>
          )}
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-1fr-1.15fr gap-4">
          <div className="min-w-0">
          <ul
            ref={listRef}
            className={`space-y-1 ${scrolls ? 'max-h-88 overflow-y-auto pr-1' : ''}`}
            aria-label={`${text.name} list`}
            data-testid="orders-workspace-list"
          >
            {visible.map((item) => {
              const key = itemKey(item);
              const active = selected ? itemKey(selected) === key : false;
              return (
                <li key={key} data-item-key={key}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedKey(key)}
                    onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedKey(key);
                      }
                    }}
                    aria-pressed={active}
                    className={cn(
                      'flex w-full items-start gap-2 rounded-md border px-2.5 py-2 text-left cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      active ? 'border-chart-5/60 bg-card' : 'border-transparent hover:bg-card/70',
                    )}
                  >
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[item.severity]}`} aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">
                        {item.row.customer || item.row.consignment_no || item.row.tracking_id}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">{item.age ? `${item.why} · ${item.age}` : item.why}</span>
                    </span>
                    <Tag item={item} />
                  </div>
                </li>
              );
            })}
          </ul>
          {scrolls && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid="orders-workspace-list-more">
              Showing {visible.length.toLocaleString()} {visible.length === 1 ? 'order' : 'orders'}, scroll for more
            </p>
          )}
          </div>
          {selected && <Detail item={selected} onOpenInTable={onOpenInTable} />}
        </div>
      )}
      {footer}
    </section>
  );
}
